/**
 * AuthProvider — versión definitiva sin condición de carrera
 *
 * PROBLEMA RAÍZ DEL "U":
 * boot() y onAuthStateChange corrían en paralelo. Ambos llamaban
 * setSession() + loadProfile() al mismo tiempo. El segundo en llegar
 * podía pisar al primero con datos incompletos → full_name null → "U"
 *
 * SOLUCIÓN:
 * - boot() hace TODO: lee sesión, carga profile, hace settle
 * - onAuthStateChange SOLO maneja eventos que ocurren DESPUÉS del boot:
 *   SIGNED_OUT, SIGNED_IN (login manual), TOKEN_REFRESHED
 * - Un lock (booting) evita que el listener interfiera durante el boot
 * - settle() solo se puede llamar una vez — garantizado por flag
 * - Profile NUNCA es null después del boot — siempre fallback con email
 */
import { useEffect }    from 'react'
import { supabase }     from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth.store'
import { startInactivityMonitor, stopInactivityMonitor } from '@/lib/inactivity'
import type { Profile } from '@/types/database'

function buildFallbackProfile(userId: string, email: string): Profile {
  return {
    id:         userId,
    username:   email,
    full_name:  email.split('@')[0],  // Nunca "U" — siempre parte del email
    email:      email,
    role:       'agente' as Profile['role'],
    active:     true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  } as Profile
}

async function loadProfile(userId: string, email: string): Promise<Profile> {
  // Intento 1: query directa con timeout 5s
  try {
    const result = await Promise.race([
      supabase.from('profiles').select('*').eq('id', userId).maybeSingle(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 5000)
      ),
    ]) as Awaited<ReturnType<typeof supabase.from<any, any>>>

    const { data, error } = result as any
    if (!error && data) {
      const p = data as Profile
      if (!p.full_name?.trim()) p.full_name = email.split('@')[0]
      return p
    }
  } catch { /* timeout o error RLS → siguiente intento */ }

  // Intento 2: RPC para obtener al menos el rol correcto
  try {
    const { data: role } = await supabase.rpc('get_user_role')
    const fb = buildFallbackProfile(userId, email)
    if (role) fb.role = role as Profile['role']
    return fb
  } catch { /* ignorar */ }

  // Fallback final garantizado — nunca retorna null
  return buildFallbackProfile(userId, email)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    let mounted  = true
    let settled  = false
    let booting  = true   // Lock: bloquea el listener durante el boot inicial

    function settle() {
      if (!mounted || settled) return
      settled = true
      booting = false
      useAuthStore.getState().setLoading(false)
    }

    async function handleExpiry() {
      stopInactivityMonitor()
      await supabase.auth.signOut()
      useAuthStore.getState().reset()
    }

    // BOOT: fuente única de verdad para la sesión inicial
    async function boot() {
      try {
        // Lee sessionde localStorage — síncrono, <50ms
        const { data: { session } } = await supabase.auth.getSession()
        if (!mounted) return

        if (!session) {
          // Sin sesión guardada → ir a login
          useAuthStore.getState().setSession(null)
          settle()
          return
        }

        // Sesión encontrada → cargar profile COMPLETO antes de settle
        // El usuario NO ve la pantalla hasta que profile esté listo
        useAuthStore.getState().setSession(session)
        const profile = await loadProfile(session.user.id, session.user.email ?? '')
        if (!mounted) return

        // Cuenta desactivada → no dejar entrar aunque el JWT siga vigente.
        // (defensa en profundidad — RLS ya lo bloquea a nivel de DB, esto
        // evita que el usuario vea siquiera la app antes de ser expulsado)
        if (profile.active === false) {
          await supabase.auth.signOut()
          useAuthStore.getState().reset()
          settle()
          window.location.href = '/login?reason=disabled'
          return
        }

        // Profile garantizado → nunca null, nunca "U"
        useAuthStore.getState().setProfile(profile)
        settle()

        // Monitor de inactividad arranca después de que el usuario entra
        startInactivityMonitor(handleExpiry)
      } catch {
        if (mounted) {
          useAuthStore.getState().setSession(null)
          settle()
        }
      } finally {
        booting = false
      }
    }

    // LISTENER: solo para eventos POST-boot
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return

        // Ignorar TODOS los eventos durante el boot — boot() los maneja
        if (booting) return

        if (event === 'SIGNED_OUT') {
          stopInactivityMonitor()
          useAuthStore.getState().reset()
          return
        }

        if (event === 'SIGNED_IN' && session) {
          // Login manual post-boot (no F5, sino login real del usuario)
          useAuthStore.getState().setSession(session)
          const profile = await loadProfile(session.user.id, session.user.email ?? '')
          if (!mounted) return

          if (profile.active === false) {
            await supabase.auth.signOut()
            useAuthStore.getState().reset()
            if (!settled) settle()
            window.location.href = '/login?reason=disabled'
            return
          }

          useAuthStore.getState().setProfile(profile)
          if (!settled) settle()
          startInactivityMonitor(handleExpiry)
          return
        }

        // TOKEN_REFRESHED / USER_UPDATED: solo actualizar token, no tocar profile
        if ((event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') && session) {
          useAuthStore.getState().setSession(session)
        }
      }
    )

    boot()

    return () => {
      mounted = false
      booting = false
      subscription.unsubscribe()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>
}
